import { Mark, Extension, Node } from '@tiptap/core'
import Table from '@tiptap/extension-table'

import { createCommand, createCommandForNode, parseClassesIntoAttributes, parseAttributesIntoClasses } from './helpers'

export const tailwindBlock = ({ device, utilities }) => Mark.create({
  name: 'tailwindBlock',

  addAttributes() {
    return {
      classes: {},
    }
  },

  parseHTML() {
    return [{
      tag: 'span',
      getAttrs: element => {
        const { classList } = element
        return { classes: parseClassesIntoAttributes(classList) }
      },
    }]
  },

  renderHTML({ HTMLAttributes: { classes } }) {
    return ['span', parseAttributesIntoClasses(classes), 0]
  },

  addCommands() {
    return {
      setFontSize: value => ({ chain, editor }) => createCommand({
        value: value ? `${value}px` : '',
        property: 'fontSize',
        editor,
        chain,
        utilities,
        device,
      }),
      setFontWeight: value => ({ chain, editor }) => createCommand({
        value: value || '',
        property: 'fontWeight',
        editor,
        chain,
        utilities,
        device,
      }),
      setLetterSpacing: value => ({ chain, editor }) => createCommand({
        value,
        property: 'letterSpacing',
        editor,
        chain,
        utilities,
        device,
      }),
      setTextColor: value => ({ chain, editor }) => createCommand({
        value: value?.rgbaParsed || value,
        property: 'textColor',
        editor,
        chain,
        utilities,
        device,
      }),
      setBackgroundColor: value => ({ chain, editor }) => createCommand({
        value: value?.rgbaParsed || value,
        property: 'backgroundColor',
        editor,
        chain,
        utilities,
        device,
      }),
      toggleBold: () => ({ chain, editor }) => createCommand({
        toggle: true,
        selectedValue: 'bold',
        unselectedValue: 'normal',
        property: 'fontWeight',
        editor,
        chain,
        utilities,
        device,
      }),
      toggleItalic: () => ({ chain, editor }) => createCommand({
        toggle: true,
        selectedValue: 'italic',
        unselectedValue: 'normal',
        property: 'fontStyle',
        editor,
        chain,
        utilities,
        device,
      }),
      toggleUnderline: () => ({ chain, editor }) => createCommand({
        toggle: true,
        selectedValue: 'underline',
        unselectedValue: 'none',
        property: 'textDecoration',
        editor,
        chain,
        utilities,
        device,
      }),
      toggleStrike: () => ({ chain, editor }) => createCommand({
        toggle: true,
        selectedValue: 'line-through',
        unselectedValue: 'none',
        property: 'textDecoration',
        editor,
        chain,
        utilities,
        device,
      }),
      setUppercase: () => ({ chain, editor }) => createCommand({
        toggle: true,
        selectedValue: 'uppercase',
        unselectedValue: 'none',
        property: 'textTransform',
        editor,
        chain,
        utilities,
        device,
      }),
      setLowercase: () => ({ chain, editor }) => createCommand({
        toggle: true,
        selectedValue: 'lowercase',
        unselectedValue: 'none',
        property: 'textTransform',
        editor,
        chain,
        utilities,
        device,
      }),
      setCapitalize: () => ({ chain, editor }) => createCommand({
        toggle: true,
        selectedValue: 'capitalize',
        unselectedValue: 'none',
        property: 'textTransform',
        editor,
        chain,
        utilities,
        device,
      }),
    }
  },
})

export const tailwindParagraph = ({ device, utilities }) => Mark.create({
  name: 'tailwindParagraph',

  addGlobalAttributes() {
    return [
      {
        types: ['paragraph', 'heading', 'table'],
        attributes: {
          classes: {
            default: [],
            parseHTML: element => {
              const { classList } = element
              return parseClassesIntoAttributes(classList)
            },
            renderHTML: attributes => parseAttributesIntoClasses(attributes.classes),
          },
        },
      },
    ]
  },

  addCommands() {
    return {
      setTextAlign: value => ({ chain, editor }) => createCommandForNode({
        value,
        property: 'textAlign',
        editor,
        chain,
        utilities,
        device,
      }),
    }
  },
})

export const tableExtension = Table.extend({
  addAttributes() {
    return {
      class: {
        default: null,
        renderHTML: () => ({
          class: `w-full [&_th]:border [&_th]:border-gray-400 [&_td]:border [&_td]:border-gray-400`.trim(),
        }),
      },
    }
  },
})

export const customParagraph = ({ device: _device, utilities: _utilities }) => Node.create({
  name: 'paragraph',
  priority: 1000,
  group: 'block',
  content: 'inline*',

  parseHTML() {
    return [
      { tag: 'p' },
      { tag: 'div' },
    ]
  },

  renderHTML({ HTMLAttributes, node }) {
    const classes = node.attrs.classes || []
    const hasTextAlign = Array.isArray(classes) && classes.some(
      classItem => classItem?.property === 'textAlign' && classItem?.value,
    )
    const tag = hasTextAlign ? 'div' : 'p'
    const classesAttrs = parseAttributesIntoClasses(classes)
    return [tag, { ...HTMLAttributes, ...classesAttrs }, 0]
  },
})

export const customList = Node.create({
  name: 'bulletList',
  group: 'block',
  content: 'listItem+',

  parseHTML() {
    return [
      {
        tag: 'ol',
        getAttrs: element => ({
          class: element.getAttribute('class') || '',
        }),
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return ['ol', { ...HTMLAttributes, class: 'list-decimal list-inside' }, 0]
  },
})

export const customOrderedList = Node.create({
  name: 'orderedList',
  group: 'block',
  content: 'listItem+',

  parseHTML() {
    return [
      {
        tag: 'ul',
        getAttrs: element => ({
          class: element.getAttribute('class') || '',
        }),
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return ['ul', { ...HTMLAttributes, class: 'list-disc list-inside' }, 0]
  },
})

export const customListItem = Node.create({
  name: 'listItem',
  group: 'listItem',
  content: 'inline*',

  parseHTML() {
    return [
      { tag: 'li' },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return ['li', HTMLAttributes, 0]
  },
})

export const orderedListExtension = ({ onChangeContent }) => Extension.create({
  name: 'orderedListTransformer',

  addCommands() {
    return {
      toggleBulletList: toggled => ({ editor }) => hardBreakToList({ listType: 'bullet', toggled, editor, onChange: html => onChangeContent({ html, editor }) }),
      toggleOrderedList: toggled => ({ editor }) => hardBreakToList({ listType: 'ordered', toggled, editor, onChange: html => onChangeContent({ html, editor }) }),
    }
  },
})

const hardBreakToList = ({ listType, toggled, editor, onChange }) => {
  const listConfig = {
    bullet: {
      tag: 'ul',
      class: 'list-disc list-inside',
    },
    ordered: {
      tag: 'ol',
      class: 'list-decimal list-inside',
    },
  }

  const list = listConfig[listType]
  const currentHtml = editor.getHTML()

  if (!toggled) {
    const updatedHtml = currentHtml.replace(/<p([^>]*)>([\s\S]*?)<\/p>/gi, (match, attrs, content) => {
      if (/<br\s*\/?>/i.test(content)) {
        const parts = content.split(/<br\s*\/?>/i)
        const validParts = parts.map(part => part.trim()).filter(part => part.length > 0)

        if (validParts.length > 0) {
          const listItems = validParts.map(part => `<li${attrs}>${part}</li>`).join('')
          return `<${list.tag} class="${list.class}">${listItems}</${list.tag}>`
        }
      }
      return match
    })

    if (updatedHtml !== currentHtml) {
      return onChange(updatedHtml)
    }

    const htmlWithTags = `<${list.tag} class="${list.class}"><li>${currentHtml.replace('<p>', '').replace('</p>', '')}</li></${list.tag}>`
    return onChange(htmlWithTags)
  }

  const removeList = currentHtml.replaceAll(`<${list.tag} `, '<p ').replaceAll(list.class, '').replaceAll(`</${list.tag}>`, '</p>').replaceAll('<li>', '').replaceAll('</li>', '<br>')
  return onChange(removeList)
}

export const enterKeyboard = Extension.create({
  name: 'enterAsBr',
  priority: 1000,
  addKeyboardShortcuts() {
    const handleEnter = () => {
      const { editor } = this

      if (editor.isActive('listItem')) {
        setTimeout(() => {
          editor.commands.splitListItem('listItem')
          const updatedHtml = editor.getHTML().replace('<br></li>', '<li></li>')
          editor.commands.setContent(updatedHtml, false)
        }, 100)
      }

      return editor.commands.setHardBreak()
    }

    return {
      Enter: handleEnter,
      'Shift-Enter': handleEnter,
    }
  },
})
